import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import { Button, Link, TextInput } from '@carbon/react';
import { Close, ShoppingBag } from '@carbon/react/icons';
import { OrderStatusEnum, OrderTypeEnum } from '@restavo/shared/enums/order';
import { roundMoney } from '@restavo/shared/money';
import SuccessIcon from 'src/components/Lottie/SuccessIcon';
import CurrentOrderAvailableTables from 'src/components/POS/CurrentOrderAvailableTables';
import CurrentOrderDeliveryInfo from 'src/components/POS/CurrentOrderDeliveryInfo';
import CurrentOrderTypes from 'src/components/POS/CurrentOrderTypes';
import OrderLine from 'src/components/POS/OrderLine';
import { canSubmitOrder } from 'src/components/POS/canSubmitOrder';
import { useMoney } from 'src/hooks/useMoney';
import { useCreateOrdersMutation, useUpdateOrdersMutation } from 'src/hooks/useOrders';
import { useCurrentRestaurantBranch } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { OrderUpdateDto } from 'src/services/apis/types';
import { usePOSStore } from 'src/states/pos';

interface CurrentOrderProps {}

const CurrentOrder = React.memo((_: CurrentOrderProps) => {
  const { t } = useTranslation('pos');
  const { format, decimals, isKnown } = useMoney();
  const [placingOrder, setPlacingOrder] = useState(false);
  const {
    isLoading: createOrdersLoading,
    isSuccess: createOrdersSuccess,
    mutateAsync: createOrders,
    reset: createOrdersReset,
  } = useCreateOrdersMutation();
  const {
    isLoading: updateOrdersLoading,
    isSuccess: updateOrdersSuccess,
    mutateAsync: updateOrders,
    reset: updateOrdersReset,
  } = useUpdateOrdersMutation();
  const currentRestaurant = useCurrentRestaurant();
  const currentRestaurantBranch = useCurrentRestaurantBranch();
  const {
    currentOrderItems,
    currentOrderTableId,
    currentOrderPhoneNumber,
    currentOrderAddress,
    currentOrderType,
    currentOrderNotes,
    clearOrderItems,
    setOrderNotes,
    clearCurrentOrder,
    editingOrder,
    cartSheetOpen,
    closeCartSheet,
  } = usePOSStore(
    (state) => ({
      currentOrderItems: state.currentOrderItems,
      clearOrderItems: state.clearOrderItems,
      currentOrderType: state.currentOrderInfo.type,
      currentOrderTableId: state.currentOrderInfo.tableId,
      currentOrderPhoneNumber: state.currentOrderInfo.phoneNumber,
      currentOrderAddress: state.currentOrderInfo.address,
      currentOrderNotes: state.currentOrderInfo.notes,
      editingOrder: state.currentOrderInfo.editingOrder,
      setOrderNotes: state.setOrderNotes,
      clearCurrentOrder: state.clearCurrentOrder,
      cartSheetOpen: state.cartSheetOpen,
      closeCartSheet: state.closeCartSheet,
    }),
    shallow
  );
  const [openAddNotes, setOpenAddNotes] = useState(false);

  useEffect(() => {
    if (currentOrderItems.length === 0 && editingOrder) {
      return clearCurrentOrder();
    }
  }, [currentOrderItems.length, editingOrder, clearCurrentOrder]);

  // The mobile sheet covers the viewport; without this the menu grid keeps scrolling
  // under it on touch devices.
  useEffect(() => {
    if (!cartSheetOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [cartSheetOpen]);

  const handlePlaceOrder = async () => {
    setPlacingOrder(true);
    const tableId = [OrderTypeEnum.DELIVERY, OrderTypeEnum.TAKEAWAY].includes(currentOrderType!)
      ? undefined
      : currentOrderTableId;
    const phoneNumber = currentOrderType === OrderTypeEnum.DELIVERY ? currentOrderPhoneNumber : undefined;
    const address = currentOrderType === OrderTypeEnum.DELIVERY ? currentOrderAddress : undefined;
    const notes = openAddNotes ? currentOrderNotes : undefined;
    if (editingOrder) {
      const payload: OrderUpdateDto & { tableId?: string | null } = {
        id: editingOrder.id,
        phoneNumber,
        line1: address,
        notes,
        tableId,
        type: currentOrderType!,
      };
      if (currentOrderType === OrderTypeEnum.DELIVERY) {
        payload.tableId = undefined;
      }
      const hasEditOrderItems =
        currentOrderItems.filter((item) => item.editing).length > 0 ||
        editingOrder.orderItems.length !== currentOrderItems.length;
      if (hasEditOrderItems) {
        const grandTotal = roundMoney(
          currentOrderItems.map((item) => item.subTotal).reduce((total, itemTotal) => total + itemTotal),
          decimals
        );
        payload.orderItems = currentOrderItems.map((orderLineItem) => ({
          menuItemId: orderLineItem.orderItem.id,
          menuItemVariantId: orderLineItem.orderItem.menuItemVariantId || undefined,
          quantity: orderLineItem.quantity,
          sku: orderLineItem.orderItem.sku,
          subTotal: roundMoney(orderLineItem.subTotal, decimals),
        }));
        payload.grandTotal = grandTotal;
        payload.subTotal = grandTotal;
      }
      await updateOrders(payload);
      clearCurrentOrder();
      setPlacingOrder(false);
      setTimeout(() => {
        updateOrdersReset();
      }, 2000);
      return;
    }
    const grandTotal = roundMoney(
      currentOrderItems.map((item) => item.subTotal).reduce((total, itemTotal) => total + itemTotal),
      decimals
    );
    await createOrders({
      restaurantId: currentRestaurant!.id,
      restaurantBranchId: currentRestaurantBranch!.id,
      tableId,
      grandTotal,
      total: grandTotal,
      type: currentOrderType!,
      tax: 0,
      orderItems: currentOrderItems.map((orderLineItem) => ({
        menuItemId: orderLineItem.orderItem.id,
        menuItemVariantId: orderLineItem.orderItem.menuItemVariantId || undefined,
        quantity: orderLineItem.quantity,
        sku: orderLineItem.orderItem.sku,
        subTotal: roundMoney(orderLineItem.subTotal, decimals),
      })),
      subTotal: grandTotal,
      status: OrderStatusEnum.IN_PROCESS,
      phoneNumber,
      line1: address,
      notes,
    });
    clearCurrentOrder();
    setOpenAddNotes(false);
    setPlacingOrder(false);
    setTimeout(() => {
      createOrdersReset();
    }, 2000);
  };

  const canSubmit = () =>
    canSubmitOrder({
      itemCount: currentOrderItems.length,
      type: currentOrderType,
      tableId: currentOrderTableId,
      phoneNumber: currentOrderPhoneNumber,
      address: currentOrderAddress,
      hasBranch: Boolean(currentRestaurantBranch),
      currencyKnown: isKnown,
    });

  const subTotal = currentOrderItems?.map((item) => item.subTotal)?.reduce((total, subTotal) => total + subTotal, 0);
  const submitOrderSuccessfully = createOrdersSuccess || updateOrdersSuccess;
  const submitting = placingOrder || createOrdersLoading || updateOrdersLoading;

  const containerClass = classNames({
    'pos-current-order': true,
    'pos-current-order--open': cartSheetOpen,
  });

  return (
    <div className={containerClass}>
      <div className="pos-current-order__header">
        <h4>{t('currentOrder')}</h4>
        <button
          type="button"
          className="pos-current-order__close"
          onClick={closeCartSheet}
          aria-label={t('closeCurrentOrder')}
        >
          <Close size={24} />
        </button>
      </div>
      {currentOrderItems.length > 0 && (
        <div className="pos-current-order-items">
          {currentOrderItems.map((row, index) => (
            <OrderLine orderLine={row} key={row.orderItem.id} index={index + 1} />
          ))}
          {(!!currentOrderNotes || openAddNotes) && (
            <TextInput
              id="notes"
              defaultValue={currentOrderNotes}
              onChange={(e) => setOrderNotes(e.target.value?.trim())}
              // Deliberate: this field opens inside a modal the operator just chose to
              // open, so focus is expected rather than stolen on page load.
              // oxlint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              labelText={t('notes')}
            />
          )}
        </div>
      )}
      {currentOrderItems.length > 0 && (
        <div className={'pos-current-order-actions'}>
          {editingOrder && <Link onClick={clearCurrentOrder}>{t('cancelEdit')}</Link>}
          {!editingOrder && <Link onClick={clearOrderItems}>{t('clearItems')}</Link>}
          <Link
            onClick={() => {
              setOpenAddNotes(!openAddNotes);
              setOrderNotes('');
            }}
          >
            {openAddNotes ? t('clearNotes') : t('addNotes')}
          </Link>
        </div>
      )}
      <div className="pos-current-order-summary">
        <div className="total-container">
          {currentOrderItems.length === 0 && (
            <div className="empty">
              {!submitOrderSuccessfully && <ShoppingBag size={56} />}
              {!submitOrderSuccessfully && <h4>{t('messages.noItemsAdded')}</h4>}
              {submitOrderSuccessfully && <SuccessIcon />}
            </div>
          )}
          {currentOrderItems.length > 0 && (
            <>
              <div className="sub-total">
                <div className="line">
                  <h4>{t('subTotal')}:</h4>
                  <h5>{format(subTotal)}</h5>
                </div>
                <div className="line">
                  <h4>{t('tax')} (0%):</h4>
                  <h5>{format(0)}</h5>
                </div>
              </div>
              <hr />
              <div className="total">
                <div className="line">
                  <h4>{t('total')}:</h4>
                  <h5>{format(subTotal)}</h5>
                </div>
              </div>
            </>
          )}
        </div>
        <CurrentOrderTypes />
        <CurrentOrderAvailableTables />
        <CurrentOrderDeliveryInfo />
        <div className="actions">
          <Button size={'xl'} disabled={!canSubmit() || submitting} onClick={handlePlaceOrder}>
            {submitting ? `${t('placingOrder')}...` : editingOrder ? t('updateOrder') : t('placeOrder')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default CurrentOrder;
